import { HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  IBusinessConfigFlags,
  IBusinessProfile,
  IPublicBusinessConfig,
  TwoWayTextSetupStatus,
} from '@g99-product-web-ui/shared-interface-lib';
import {
  API_ENDPOINTS,
  LOCAL_STORAGE_KEYS,
} from '@g99-product-web-ui/shared-util-lib';
import { Observable } from 'rxjs';

import { ServiceType } from '../../constants/service-types.enum';
import { HttpService } from '../../core/http-service/http.service';
import { SKIP_401_HANDLER } from '../../core/interceptors/ auth-context';
import { ISharedGoogleSetupResponse } from '../../interfaces/google-setup-response.interface';
import { LocalStorageService } from '../helper-service/localStorage.service';
import { SubjectManageService } from '../helper-service/subject-manager.service';

@Injectable({
  providedIn: 'root',
})
export class SharedBusinessService {
  constructor(
    private readonly _httpService: HttpService,
    private _localStorageService: LocalStorageService,
    private readonly _subjectManagerService: SubjectManageService
  ) {}

  /**
   * Fetches business profile information from the API by given business ID.
   *
   * @param bid The business ID.
   * @returns An observable containing the business profile information.
   */

  public fetchBusinessProfile(bid: number): Observable<IPublicBusinessConfig> {
    const url = API_ENDPOINTS.BUSINESS_ENDPOINTS.v1businessProfile(bid);

    const options = {
      attachTenantId: false,
      headers: new HttpHeaders().set('x-tenantId', String(bid)),
    };

    // Utilize the HttpService's get method
    return this._httpService.get<IPublicBusinessConfig>(
      url,
      options,
      ServiceType.DEFAULT
    );
  }

  /**
   * Updates the business name and notifies any subscribers.
   *
   * This method takes a new business name as input and updates the internal
   * state by emitting the new value through the `businessNameSubject` subject.
   * Subscribers to this subject will be notified with the updated business name.
   * Also updates the local storage value when the business value updated.
   *
   * @param {string} businessName - The new business name to set.
   */
  public updateBusinessName(businessData: IBusinessProfile) {
    const businessInfo =
      this._localStorageService.readItem<IPublicBusinessConfig>('businessInfo');

    if (businessInfo && businessData) {
      businessInfo.name = businessData.name;
      businessInfo.logoUrl = businessData.logoUrl;
      this._localStorageService.storeItem('businessInfo', businessInfo);
      this._subjectManagerService.businessSubject.next(businessInfo);
    }
  }

  public updateUserExperiencePreference(bid: number, obj: any) {
    const apiUrl = `${API_ENDPOINTS.BUSINESS_ENDPOINTS.updateBusinessConfig(bid)}`;
    return this._httpService.patch<any, void>(apiUrl, obj);
  }

  /**
   * Determines whether to show the A2P setup banner based on business setup status.
   *
   * @returns {{
   *   registrationStatus: TwoWayTextSetupStatus,
   *   isSetupPendingOrWaitingApproval: boolean
   * }} - Setup status and banner visibility.
   */
  public getTwoWayTextBannerStatus(): {
    registrationStatus: TwoWayTextSetupStatus;
    isSetupPendingOrWaitingApproval: boolean;
    twilioNumber?: string | null;
  } {
    const businessInfo: IPublicBusinessConfig | null =
      this._localStorageService.readItem(LOCAL_STORAGE_KEYS.BUSINESS_INFO);

    const registrationStatus =
      businessInfo?.A2pSetUp || TwoWayTextSetupStatus.SETUP_PENDING;

    const isSetupPendingOrWaitingApproval =
      registrationStatus !== TwoWayTextSetupStatus.APPROVAL_DONE;

    const twilioNumber = businessInfo?.twilioNumber;
    return {
      registrationStatus,
      isSetupPendingOrWaitingApproval,
      twilioNumber,
    };
  }

  /**
   * Marks two-way text as activated in the cached business info after a
   * support/super-admin manual activation, so the "Activate Now" banner and
   * button hide immediately without waiting for the next full page load.
   */
  public markTwoWayTextActivated(twilioNumber: string): void {
    const businessInfo =
      this._localStorageService.readItem<IPublicBusinessConfig>(
        LOCAL_STORAGE_KEYS.BUSINESS_INFO
      );

    if (!businessInfo) {
      return;
    }

    businessInfo.A2pSetUp = TwoWayTextSetupStatus.APPROVAL_DONE;
    businessInfo.twilioNumber = twilioNumber;

    this._localStorageService.storeItem(
      LOCAL_STORAGE_KEYS.BUSINESS_INFO,
      businessInfo
    );
    this._subjectManagerService.businessSubject.next(businessInfo);
  }

  /**
   * Resets two-way text in the cached business info after a
   * support/super-admin deactivation, so the UI reverts to the not-set-up
   * state immediately without waiting for the next full page load.
   */
  public markTwoWayTextDeactivated(): void {
    const businessInfo =
      this._localStorageService.readItem<IPublicBusinessConfig>(
        LOCAL_STORAGE_KEYS.BUSINESS_INFO
      );

    if (!businessInfo) {
      return;
    }

    businessInfo.A2pSetUp = TwoWayTextSetupStatus.SETUP_PENDING;
    businessInfo.twilioNumber = null;

    this._localStorageService.storeItem(
      LOCAL_STORAGE_KEYS.BUSINESS_INFO,
      businessInfo
    );
    this._subjectManagerService.businessSubject.next(businessInfo);
  }

  public getGoogleSetupStatus(): Observable<ISharedGoogleSetupResponse> {
    const url = API_ENDPOINTS.REVIEWS.googleSetupStatus;
    return this._httpService.get<ISharedGoogleSetupResponse>(url);
  }

  public getBusinessConfig(bid: number): Observable<IBusinessConfigFlags> {
    const url = API_ENDPOINTS.BUSINESS_ENDPOINTS.getBusinessConfig(bid);
    return this._httpService.get<IBusinessConfigFlags>(url, {
      attachTenantId: true,
      responseType: 'json',
      // A transient 401 here (e.g. right after a fresh support/session login)
      // shouldn't blow away the session — the caller already handles failures.
      context: new HttpContext().set(SKIP_401_HANDLER, true),
    });
  }

  /**
   * Fetches the latest business config, caches it, and re-emits businessSubject
   * so flag-gated UI (nav, trigger tabs) rebuilds with the current flags.
   * Called on login and on every app bootstrap/refresh so changes made in the
   * admin panel take effect on the next refresh.
   */
  public refreshBusinessConfig(bid: number): void {
    this.getBusinessConfig(bid).subscribe((businessConfig) => {
      this._localStorageService.storeItem(
        LOCAL_STORAGE_KEYS.BUSINESS_CONFIG,
        businessConfig
      );
      const businessInfo =
        this._localStorageService.readItem<IPublicBusinessConfig>(
          LOCAL_STORAGE_KEYS.BUSINESS_INFO
        );
      if (businessInfo) {
        this._subjectManagerService.businessSubject.next(businessInfo);
      }
    });
  }

  /**
   * Reads the appointment feature flag from the cached business config.
   * Drives hiding the Appointment nav module and trigger tab when disabled.
   */
  public isAppointmentEnabled(): boolean {
    const config: IBusinessConfigFlags | null =
      this._localStorageService.readItem(LOCAL_STORAGE_KEYS.BUSINESS_CONFIG);
    return config?.isAppointmentEnabled === true;
  }

  /** Whether Gia (AI responder) is active for the current business. */
  public isGiaActive(): boolean {
    const config: IBusinessConfigFlags | null =
      this._localStorageService.readItem(LOCAL_STORAGE_KEYS.BUSINESS_CONFIG);
    return config?.isAiResponderEnabled === true;
  }

  /**
   * Reads the AI Campaign feature flag from the cached business config.
   * When true (and Gia is active) the Automation tab shows AI Campaign in place of Trigger.
   */
  public isAICampaignEnabled(): boolean {
    const config: IBusinessConfigFlags | null =
      this._localStorageService.readItem(LOCAL_STORAGE_KEYS.BUSINESS_CONFIG);
    return config?.isAICampaignEnabled === true;
  }

  public updateBusinessConfig(
    bid: number,
    businessConfig: Partial<IPublicBusinessConfig>
  ): Observable<IPublicBusinessConfig> {
    const url = API_ENDPOINTS.BUSINESS_ENDPOINTS.updateBusinessConfig(bid);
    return this._httpService.patch(url, businessConfig);
  }
}
